import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';
import PopupCard from '../MainComponents/PopupCard';
import { useTranslation } from 'react-i18next';
import './CompanyBar.css';

export default function CompanyOrders() {
  const { t } = useTranslation();
  const [companyProfile, setCompanyProfile]     = useState(null);
  const [orders, setOrders]                     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [selectedOrder, setSelectedOrder]       = useState(null);
  const [activeTab, setActiveTab]               = useState('pending'); // 'pending', 'preparing', 'ready'

  /* ── Track auth state & subscribe to orders ── */
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setCompanyProfile(null);
        setOrders([]);
        setLoading(false);
        return;
      }

      // Fetch company profile (one-time)
      const userRef = doc(db, 'users', user.uid);
      onSnapshot(userRef, (snap) => {
        if (snap.exists()) setCompanyProfile(snap.data());
      }, (err) => console.error('[CompanyOrders] profile error:', err));

      // Listen to all company orders
      const ordersQ = query(collection(db, 'orders'), where('companyId', '==', user.uid));
      const unsubOrders = onSnapshot(ordersQ, (snap) => {
        const fetchedOrders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort by creation time: newest first
        fetchedOrders.sort((a, b) => {
          const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt === null ? Date.now() : new Date(a.createdAt || 0).getTime());
          const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt === null ? Date.now() : new Date(b.createdAt || 0).getTime());
          return timeB - timeA;
        });
        setOrders(fetchedOrders);
        setLoading(false);
      }, (err) => {
        console.error('[CompanyOrders] orders query error:', err);
        setLoading(false);
      });

      return () => unsubOrders();
    });

    return () => unsubAuth();
  }, []);

  /* ── Live prep actions ── */
  const handleAcceptOrder = async (orderId, readyInMins) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'accepted',
        readyInMins: readyInMins || 15,
        acceptedAt: new Date().toISOString()
      });
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error accepting order:", error);
    }
  };

  const handleMarkReady = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'ready',
        readyAt: new Date().toISOString()
      });
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error marking order ready:", error);
    }
  };

  const handleRejectOrder = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'cancelled',
        cancelledAt: new Date().toISOString()
      });
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error cancelling order:", error);
    }
  };

  const calculateMinsAgo = (timestamp) => {
    if (!timestamp) return 0;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = new Date() - date;
    return Math.max(0, Math.floor(diffMs / 60000));
  };

  /* ── Stats Calculations ── */
  const avgPrepTime = (() => {
    const preppedOrders = orders.filter(o => o.acceptedAt && o.readyAt && o.status !== 'cancelled');
    if (preppedOrders.length === 0) return '—';
    const times = preppedOrders.map(o => {
      const start = new Date(o.acceptedAt);
      const end = new Date(o.readyAt);
      return (end - start) / 60000;
    }).filter(t => t > 0);
    if (times.length === 0) return '—';
    return (Math.round(times.reduce((sum, t) => sum + t, 0) / times.length * 10) / 10) + 'm';
  })();

  const dailyRevenue = orders
    .filter(o => {
      if (!['completed', 'ready', 'delivering', 'pickingup'].includes(o.status)) return false;
      const date = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      return new Date().toDateString() === date.toDateString();
    })
    .reduce((sum, o) => sum + Number(o.total || 0), 0);

  const rating = Number(companyProfile?.rating || 4.8);
  const stars  = '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating));

  /* ── Filter orders by tab status ── */
  const pendingOrders   = orders.filter(o => o.status === 'pending');
  const preparingOrders = orders.filter(o => o.status === 'accepted');
  const readyOrders     = orders.filter(o => o.status === 'ready');

  const displayOrders = activeTab === 'pending'
    ? pendingOrders
    : activeTab === 'preparing'
      ? preparingOrders
      : readyOrders;

  return (
    <div className="company-dashboard">
      {/* ── Header Row ── */}
      <div className="cb-header-row">
        <div>
          <h1 className="cb-title">{t('companyOrders.title', 'Live Orders')}</h1>
          <p className="cb-subtitle">{t('companyOrders.subtitle', 'Manage incoming orders and track preparation in real-time.')}</p>
        </div>
        <div className="cb-header-actions">
          <button className="cb-btn cb-btn-teal">{t('companyOrders.liveView', 'Live View')}</button>
          <button className="cb-btn cb-btn-dark">{t('companyOrders.kitchenDisplay', 'Kitchen Display')}</button>
        </div>
      </div>

      {/* ── Tabs Row ── */}
      <div className="cb-tabs-row">
        <div className="cb-tabs">
          <div
            className={`cb-tab ${activeTab === 'pending' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            {t('companyOrders.tabPending', 'Pending')} ({pendingOrders.length})
          </div>
          <div
            className={`cb-tab ${activeTab === 'preparing' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('preparing')}
          >
            {t('companyOrders.tabPreparing', 'Preparing')} ({preparingOrders.length})
          </div>
          <div
            className={`cb-tab ${activeTab === 'ready' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('ready')}
          >
            {t('companyOrders.tabReadyActive', 'Ready/Active')} ({readyOrders.length})
          </div>
        </div>
        <div className="cb-live-sync">
          <span className="sync-dot"></span> {t('companyOrders.liveSync', 'Live Syncing')}
        </div>
      </div>

      {/* ── Orders list ── */}
      <div className="cb-orders-list">
        {loading ? (
          <div style={{ color: '#8a8f9e', padding: '20px' }}>{t('companyOrders.loadingOrders', 'Loading orders...')}</div>
        ) : displayOrders.length === 0 ? (
          <div style={{ color: '#8a8f9e', padding: '20px' }}>
            {t('companyOrders.emptyOrders', 'No {{status}} orders.').replace('{{status}}', t(`companyOrders.tab${activeTab[0].toUpperCase() + activeTab.slice(1)}`, activeTab))}
          </div>
        ) : (
          displayOrders.map(order => {
            const minsAgo = calculateMinsAgo(order.createdAt);
            const isUrgent = minsAgo > 10;

            return (
              <div
                key={order.id}
                className={`cb-order-card ${isUrgent ? 'cb-order-card--urgent' : ''}`}
                onClick={() => setSelectedOrder(order)}
                style={{ cursor: 'pointer' }}
              >
                {/* Left accent bar */}
                <div className={`cb-card-accent ${isUrgent ? 'cb-card-accent--urgent' : ''}`} />

                {/* Customer avatar */}
                <div className={`cb-avatar ${isUrgent ? 'cb-avatar--urgent' : ''}`}>
                  {(order.customerName || 'U')[0].toUpperCase()}
                </div>

                {/* Customer + Order ID */}
                <div className="cb-customer-col">
                  <div className="cb-col-lbl">{t('companyOrders.customer', 'CUSTOMER')}</div>
                  <div className="cb-customer-name">{order.customerName || 'Unknown Customer'}</div>
                  <div className="cb-order-id">#{order.orderId || order.id.slice(0, 8).toUpperCase()}</div>
                </div>

                {/* Divider */}
                <div className="cb-col-divider" />

                {/* Order items */}
                <div className="cb-items-col">
                  <div className="cb-col-lbl">{t('companyOrders.orderItems', 'ORDER ITEMS')}</div>
                  <div className="cb-items-text">{order.itemsText || '1x Item'}</div>
                  <div className="cb-badges">
                    {(order.tags || []).map((tag, i) => (
                      <span key={i} className={`cb-badge ${tag.includes('SPICY') ? 'orange-badge' : ''}`}>{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div className="cb-col-divider" />

                {/* Time */}
                <div className="cb-time-col">
                  <div className="cb-col-lbl">{t('companyOrders.waitTime', 'WAIT TIME')}</div>
                  <div className={`cb-time-num ${isUrgent ? 'cb-time-num--urgent' : ''}`}>
                    {String(minsAgo).padStart(2, '0')}<span className="cb-time-unit">m</span>
                  </div>
                  {isUrgent && <div className="cb-urgent-tag">⚡ {t('companyOrders.urgent', 'Urgent')}</div>}
                </div>

                {/* Divider */}
                <div className="cb-col-divider" />

                {/* Total */}
                <div className="cb-total-col">
                  <div className="cb-col-lbl">{t('companyOrders.total', 'TOTAL')}</div>
                  <div className="cb-total-val">${Number(order.total || 0).toFixed(2)}</div>
                  <div className="cb-payment">{order.paymentMethod || 'Card'}</div>
                </div>

                {/* Actions */}
                <div className="cb-actions-col">
                  {order.status === 'pending' && (
                    <>
                      <button
                        className="cb-btn-accept"
                        onClick={(e) => { e.stopPropagation(); handleAcceptOrder(order.id, 15); }}
                      >
                        ✓ {t('companyOrders.acceptBtn', 'Accept')}
                      </button>
                      <button
                        className="cb-btn-reject"
                        onClick={(e) => { e.stopPropagation(); handleRejectOrder(order.id); }}
                      >
                        ✕ {t('companyOrders.rejectBtn', 'Reject')}
                      </button>
                    </>
                  )}
                  {order.status === 'accepted' && (
                    <>
                      <button
                        className="cb-btn-accept"
                        style={{ background: '#22c55e' }}
                        onClick={(e) => { e.stopPropagation(); handleMarkReady(order.id); }}
                      >
                        ✓ {t('companyOrders.readyBtn', 'Ready')}
                      </button>
                      <button
                        className="cb-btn-reject"
                        onClick={(e) => { e.stopPropagation(); handleRejectOrder(order.id); }}
                      >
                        ✕ {t('companyOrders.cancelBtn', 'Cancel')}
                      </button>
                    </>
                  )}
                  {['ready', 'delivering', 'completed'].includes(order.status) && (
                    <span style={{
                      color: order.status === 'completed' ? '#22c55e' : order.status === 'delivering' ? '#a855f7' : '#3b82f6',
                      fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em'
                    }}>
                      ● {order.status}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Stats Row ── */}
      <div className="cb-stats-row">
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">{t('companyOrders.avgPrepTime', 'AVG. PREP TIME')}</div>
          <div className="cb-stat-val">{avgPrepTime}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">{t('companyOrders.dailyRevenue', 'DAILY REVENUE')}</div>
          <div className="cb-stat-val">${dailyRevenue.toFixed(2)}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">{t('companyOrders.totalOrders', 'TOTAL ORDERS')}</div>
          <div className="cb-stat-val">{orders.length}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">{t('companyOrders.rating', 'MERCHANT RATING')}</div>
          <div className="cb-stat-val">
            {rating.toFixed(1)} <span className="cb-stars">{stars}</span>
          </div>
        </div>
      </div>

      {/* ── Order Detail Modal ── */}
      {selectedOrder && (
        <div className="cb-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <PopupCard
              category={selectedOrder.tags && selectedOrder.tags.length > 0 ? selectedOrder.tags[0] : 'ORDER DETAILS'}
              title={selectedOrder.itemsText}
              customerName={selectedOrder.customerName}
              address={selectedOrder.address}
              orderId={selectedOrder.orderId || selectedOrder.id.slice(0, 8).toUpperCase()}
              orderTime={
                selectedOrder.createdAt
                  ? new Date(selectedOrder.createdAt.toDate ? selectedOrder.createdAt.toDate() : selectedOrder.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }
              isNew={selectedOrder.status === 'pending'}
              showTimePicker={selectedOrder.status === 'pending'}
              readyInMins={selectedOrder.readyInMins}
              onRemove={() => handleRejectOrder(selectedOrder.id)}
              onAccept={
                selectedOrder.status === 'pending'
                  ? (mins) => handleAcceptOrder(selectedOrder.id, mins)
                  : () => handleMarkReady(selectedOrder.id)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
